import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { UiErrorParser } from '../ui-error-parser';

/**
 * Devolve o contexto da operação nas falhas de **leitura** das telas de configuração.
 *
 * <p>Um `GET` de listagem não tem o que validar: quando falha, é infraestrutura, e o backend
 * responde com a mensagem genérica do `CustomExceptionHandler` ("Não foi possível concluir a
 * operação no banco de dados…"). Sozinha, ela não diz **qual** recurso falhou. Este parser
 * reconstrói esse pedaço a partir da URL — mesmo recurso do `PrestadorErrorParser` —, em vez de
 * espalhar `try/catch` de relabel pelos services do backend.
 *
 * <p>Escrita não passa por aqui: `salvar`/`excluir` já chegam com a regra de negócio no `title`
 * (ex.: "Banco não encontrado.", "UF obrigatória"), que é mais específica do que qualquer texto
 * fixo. Por isso só assumimos o contexto quando o backend **não** mandou mensagem própria.
 */
@Injectable()
export class ConfiguracoesErrorParser extends UiErrorParser {
    /** Trecho da URL → nome do recurso, na ordem em que deve ser testado (mais específico antes). */
    private readonly alvosPorRota: ReadonlyArray<readonly [string, string]> = [
        ['/arrecadacao/banco-conta', 'as contas do banco'],
        ['/arrecadacao/bancos', 'os bancos'],
        ['/arrecadacao/tipo-convenio', 'os tipos de convênio'],
        ['/arrecadacao/convenio', 'os convênios'],
        ['/arrecadacao/conta-contabil', 'a configuração de contabilidade'],
        ['/arrecadacao/tipo-debito', 'os tipos de débito'],
        ['/arrecadacao/indicadores', 'os indicadores'],
        ['/arrecadacao/refis-portaria', 'as portarias do Refis'],
        ['/divida-ativa/assinante', 'os assinantes'],
        ['/divida-ativa/documento', 'os tipos de documento'],
        ['/divida-ativa/entidade', 'as entidades'],
        ['/divida-ativa/configuracao', 'a configuração da Dívida Ativa'],
        ['/sistema/registro/estruturas', 'as estruturas organizacionais'],
        ['/sistema/usuarios/permissoes', 'as permissões do usuário'],
        ['/sistema/usuarios', 'os usuários'],
        ['/sistema/config-email', 'as configurações de e-mail'],
        ['/portal/modulo', 'os módulos'],
        ['/portal/servico', 'os serviços'],
        ['/api/roles', 'as roles'],
        ['/api/usuarios', 'os perfis']
    ];

    constructor() {
        super();
        this.setType('unknown');
    }

    /** Só entra em leitura (`GET`) das rotas mapeadas — escrita fica com a mensagem do backend. */
    public override verifyContext(error: HttpErrorResponse, req: HttpRequest<unknown>): boolean {
        if (req.method !== 'GET' || error.status === 0) {
            return false;
        }
        return this.resolverAlvo(req.url) !== null;
    }

    public override beforeTextBuild(error: HttpErrorResponse, req: HttpRequest<unknown>): void {
        const isRegraDeNegocio = error.status >= 400 && error.status < 500;
        this.setTitle(isRegraDeNegocio ? 'Atenção' : 'Erro');
        this.setSeverity(isRegraDeNegocio ? 'warn' : 'error');
        this.setMethod('');
        this.setTarget('');

        // Mensagem do backend ganha da nossa: só ela conhece a regra que falhou.
        const doBackend = this.extractErrorMessage(error);
        if (doBackend) {
            this.setMessageStart(doBackend);
            return;
        }

        this.setMessageStart(`Não foi possível carregar ${this.resolverAlvo(req.url) ?? 'os dados'}.`);
    }

    public override errorComplement(): string {
        return '';
    }

    private resolverAlvo(url: string): string | null {
        for (const [trecho, alvo] of this.alvosPorRota) {
            if (url.includes(trecho)) {
                return alvo;
            }
        }
        return null;
    }
}
